import { financialDocumentContract } from "@common/contracts/financial-document.contract";
import { FinancialDocumentExtendedDto } from "@common/dto/financialDocument/financial-document-extended.dto";
import { FetchFinancialDocumentsResponse } from "@common/dto/financialDocument/fetch-financial-documents-response.dto";
import { UploadFinancialDocumentDto } from "@common/dto/financialDocument/upload-financial-document.dto";
import { ChangeEntityForFinancialDocumentDto } from "@common/dto/financialDocument/change-entity-for-financial-document.dto";
import { CreateLinkFinancialDocumentAndTransactionDto } from "@common/dto/financialDocument/create-link-financial-document-and-transaction.dto";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { FinancialDocumentQuery } from "@common/schemas/financialDocument/financial-document-query.schema";
import { FinancialDocumentDetailIncludeQuery } from "@common/schemas/financialDocument/financial-document-detail-include.schema";
import { deserializeFinancialDocumentListItemFromSchema } from "@common/dto/financialDocument/deserialize-financial-document-list-item-from-schema";
import { backendClient, unwrapResponse } from "./backendClient";
import { getBackendAPIClient } from "./backendAPIClient";

const financialDocumentContractClient = getBackendAPIClient(
    financialDocumentContract,
    undefined,
    { jsonQuery: true },
);

const financialDocumentContractBlobClient = getBackendAPIClient(
    financialDocumentContract,
    "blob",
);

export async function queryFinancialDocuments(
    params: FinancialDocumentQuery,
): Promise<FetchFinancialDocumentsResponse> {
    const result = await financialDocumentContractClient.getDocuments({
        query: params,
    });
    if (result.status !== 200) {
        throw new Error("Failed to fetch financial documents");
    }

    const { data, total, pageCount } = result.body;
    return {
        total,
        pageCount,
        data: data.map(deserializeFinancialDocumentListItemFromSchema),
    };
}

export async function fetchFinancialDocument(
    id: number,
    includeQuery?: FinancialDocumentDetailIncludeQuery,
) {
    const result = await financialDocumentContractClient.getDocument({
        params: { financialDocumentId: id },
        query: includeQuery ?? {},
    });
    return FinancialDocumentExtendedDto.deserialize(result.body);
}

export async function getFinancialDocumentFile(id: number) {
    return await financialDocumentContractBlobClient.getDocumentFile({
        params: { financialDocumentId: id },
    });
}

export async function uploadDocument(data: UploadFinancialDocumentDto) {
    const result = await financialDocumentContractClient.uploadDocument({
        body: data,
    });
    if (result.status !== 201) {
        throw new Error("Failed to upload financial document");
    }

    return FinancialDocumentExtendedDto.deserialize(result.body);
}

export async function detachDocumentFromFinancialTransaction(
    financialDocumentId: number,
    matchId: number,
) {
    const response = unwrapResponse(
        await backendClient.post(
            `/financial-document/${financialDocumentId}/detach/${matchId}`,
        ),
    );

    return FinancialDocumentExtendedDto.deserialize(response);
}

export async function changeEntityForFinancialDocument(
    data: ChangeEntityForFinancialDocumentDto,
) {
    const response = unwrapResponse(
        await backendClient.post(`/financial-document/change-entity`, data),
    );

    return FinancialDocumentExtendedDto.deserialize(response);
}

export async function createLinkFinancialDocumentToFinancialTransaction(
    payload: CreateLinkFinancialDocumentAndTransactionDto,
): Promise<FinancialDocumentExtended> {
    const response = unwrapResponse(
        await backendClient.post(`/financial-document/link`, payload),
    );

    return FinancialDocumentExtendedDto.deserialize(response);
}

export async function runTaxonomyExtraction(
    financialDocumentId: number,
): Promise<void> {
    unwrapResponse(
        await backendClient.post(
            `/financial-document/${financialDocumentId}/taxonomy-extraction`,
        ),
    );
}
